import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Handles collection/search pagination without a full page reload.
   * Merges pagination params onto the current URL so active filters are preserved.
   *
   * @param {Record<string, string | number>} data - URL search params from the pagination link
   * @param {Event} event
   */
  onPaginationClick(data, event) {
    event.preventDefault();

    const sectionId = this.getAttribute('section-id');
    if (!sectionId) return;

    const facetsForm = document.querySelector(`facets-form-component[section-id="${sectionId}"]`);
    if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') return;

    const url = new URL(window.location.href);

    for (const [key, value] of Object.entries(data)) {
      url.searchParams.set(key, String(value));
    }

    if (url.searchParams.get('page') === '1') {
      url.searchParams.delete('page');
    }

    facetsForm.updateFiltersByURL(url.toString());
    this.#scrollToTopOfResults();
  }

  #scrollToTopOfResults() {
    requestAnimationFrame(() => {
      const resultsAnchor = document.getElementById('ResultsList') ?? this;
      const top = resultsAnchor.getBoundingClientRect().top;
      scrollTo({ top: top + getScrollTop(), behavior: 'smooth' });
    });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
